// ============================================================================
// PQMark — the PQ Intelligence brand marks, as components.
// ----------------------------------------------------------------------------
// PQ is marketed as its own product, so the app shows its real marks instead
// of a generic icon (John, 2026-09-26). Two assets live in /public/brand:
//
//   pq-shield.webp   — the copper shield badge (transparent). Use ≥14px; the
//                      silhouette carries it small, the detail needs ~40px+.
//   pq-wordmark.webp — the horizontal "PQ | INTELLIGENCE" wordmark
//                      (transparent, copper letters with dark outline; reads
//                      on both light and dark surfaces).
//   pq-monogram.webp — "PQ|" alone (letters + divider bar), cut from the
//                      wordmark, for use inside button labels.
//
// Raster marks scale DOWN only — never stretch above natural size (shield
// 271×320, wordmark 900×262, monogram 102×96). Brand rules per
// docs/brand_spec.md.
// ============================================================================
import React from 'react';
import { cn } from '@/lib/utils';

/** The PQ shield badge. `size` is the rendered height in px. */
export const PQShield = ({ size = 20, className, ...rest }) => (
  <img
    src="/brand/pq-shield.webp"
    alt="PQ Intelligence"
    height={size}
    width={Math.round(size * (271 / 320))}
    style={{ height: size, width: 'auto' }}
    className={cn('inline-block select-none shrink-0', className)}
    draggable={false}
    loading="lazy"
    {...rest}
  />
);

/** The PQ | INTELLIGENCE wordmark. `height` is the rendered height in px. */
export const PQWordmark = ({ height = 16, className, ...rest }) => (
  <img
    src="/brand/pq-wordmark.webp"
    alt="PQ Intelligence"
    style={{ height, width: 'auto' }}
    className={cn('inline-block select-none', className)}
    draggable={false}
    loading="lazy"
    {...rest}
  />
);

/**
 * The "PQ|" monogram — the PQ letters plus divider bar, cut from the
 * wordmark. Used INSIDE text labels where the word "PQ" would appear
 * ("Ask PQ", "PQ RE-View"), so it sits on the text baseline. `height` is
 * the rendered height in px; ~1.4× the label's font size looks right.
 */
export const PQMonogram = ({ height = 14, className, ...rest }) => (
  <img
    src="/brand/pq-monogram.webp"
    alt="PQ"
    style={{ height, width: 'auto' }}
    className={cn('inline-block select-none align-middle shrink-0', className)}
    draggable={false}
    {...rest}
  />
);

/**
 * A PQ action label: the PQ| monogram beside a dark "pill" of glowing text,
 * per John's lockups (2026-09-29): "PQ| [RE-VIEW]" and "[ASK] PQ|".
 * `monogram` = 'before' | 'after'. Reserved for things PQ actually does —
 * the mark has to mean "PQ is working here" or it stops meaning anything.
 */
export const PQActionLabel = ({ label, monogram = 'before', height = 30 }) => (
  <span className="inline-flex items-center gap-1.5">
    {monogram === 'before' ? <PQMonogram height={height} /> : null}
    <span className="pq-pill">{label}</span>
    {monogram === 'after' ? <PQMonogram height={height} /> : null}
  </span>
);

export default PQShield;
